import { useCallback, useEffect, useRef } from "react";
import { useBusyBlockerContext } from "../../../contexts/busy-blocker-context";
import { useErrorHandling } from "../../../hooks/useErrorHandling";
import { useFontFamilies } from "../../../hooks/useFontFamilies";
import { useLabels } from "../../../hooks/useLabels";
import { useSelectedZone } from "../../../hooks/useSelection";
import { useTextZoneDashboard } from "../../../hooks/useTextZoneDashboard";
import { labels } from "../text-style-panel/labels";
import { jsonComparer, useMessageToState } from "../../../hooks/useMessageToState";
import { useMessage } from "../../../hooks/useMessage";
import { IconName } from "../../icon";
import { IStopPropagation } from "../../../utils/types";

export function useTextStyleButtons() {
	const [[labelBold, labelItalic, labelUnderline]] = useLabels([
		labels.bold,
		labels.italic,
		labels.underline,
	]);

	const { busyBlocker } = useBusyBlockerContext();
	const textZoneDashboard = useTextZoneDashboard();
	const { createErrorHandler } = useErrorHandling();
	const selectedZone = useSelectedZone();

	// Make sure all info for font is available (rerenders after font families are loaded)
	// isBoldSupported and isItalicSupported needs to be set for the current font
	const { isFetched: areFontFamiliesLoaded } = useFontFamilies();

	const textZone = eXpress.core.isAnyTextZone(selectedZone) ? selectedZone : undefined;

	// Keep the original font size to prevent it from changing when toggling bold/italic (as some fonts have different font sizes for those styles)
	const initialFontSizeInPtRef = useRef(textZone?.textSettings.ptFontSize);

	// Update initial font size when selected textzone changes
	useEffect(() => {
		initialFontSizeInPtRef.current = textZone?.textSettings.ptFontSize;
	}, [textZone]);

	// Update initial font size when font size changes within the same zone (e.g., user set font size in the top menu)
	useMessage(
		useCallback(
			(message) => {
				if (
					textZone &&
					message instanceof eXpress.core.TextSizeChangeMessage &&
					(eXpress.core.isZoneAffected(message, selectedZone) ||
						eXpress.core.isSelectedZoneChanged(message))
				) {
					initialFontSizeInPtRef.current = textZone.textSettings.ptFontSize;
				}
			},
			[textZone, selectedZone],
		),
		{ captureInteractiveMessages: true },
	);

	const getStateFromZone = useCallback(
		() => ({
			isBold: !!textZone?.textSettings.isBold,
			isBoldDisabled: !textZoneDashboard.canSetTextBold(textZone),
			isItalic: !!textZone?.textSettings.isItalic,
			isItalicDisabled: !textZoneDashboard.canSetTextItalic(textZone),
			isUnderlined: !!textZone?.textSettings.isUnderlined,
			isUnderlinedDisabled: !textZoneDashboard.canSetTextUnderline(textZone),
		}),
		// Re-evaluate if fonts are loaded
		// eslint-disable-next-line react-compiler/react-compiler
		// eslint-disable-next-line react-hooks/exhaustive-deps
		[textZoneDashboard, textZone, areFontFamiliesLoaded],
	);

	const {
		isBold,
		isBoldDisabled,
		isItalic,
		isItalicDisabled,
		isUnderlined,
		isUnderlinedDisabled,
	} = useMessageToState({
		name: "useTextStyleButtons",
		getState: getStateFromZone,
		filterMessage: (message) =>
			eXpress.core.isZoneAffected(message, selectedZone) ||
			eXpress.core.isSelectedZoneChanged(message),
		comparer: jsonComparer,
	});

	const onBoldClick = useCallback(
		(e?: IStopPropagation) => {
			e?.stopPropagation();
			if (textZone) {
				busyBlocker
					.during(
						textZoneDashboard.setTextBoldAsync(textZone, !isBold, {
							preferedFontSizeInPt: initialFontSizeInPtRef.current,
						}),
					)
					.catch(createErrorHandler());
			}
		},
		[textZone, busyBlocker, textZoneDashboard, isBold, createErrorHandler],
	);

	const onItalicClick = useCallback(
		(e?: IStopPropagation) => {
			e?.stopPropagation();
			if (textZone) {
				busyBlocker
					.during(
						textZoneDashboard.setTextItalicAsync(textZone, !isItalic, {
							preferedFontSizeInPt: initialFontSizeInPtRef.current,
						}),
					)
					.catch(createErrorHandler());
			}
		},
		[textZone, busyBlocker, textZoneDashboard, isItalic, createErrorHandler],
	);

	const onUnderlineClick = useCallback(
		(e?: IStopPropagation) => {
			e?.stopPropagation();
			if (textZone) {
				busyBlocker
					.during(textZoneDashboard.setTextUnderlineAsync(textZone, !isUnderlined))
					.catch(createErrorHandler());
			}
		},
		[textZone, busyBlocker, textZoneDashboard, isUnderlined, createErrorHandler],
	);

	return {
		bold: {
			iconName: "text-style-bold" as IconName,
			label: labelBold,
			isActive: isBold,
			isDisabled: isBoldDisabled || !areFontFamiliesLoaded,
			onClick: onBoldClick,
		},
		italic: {
			iconName: "text-style-italic" as IconName,
			label: labelItalic,
			isActive: isItalic,
			isDisabled: isItalicDisabled || !areFontFamiliesLoaded,
			onClick: onItalicClick,
		},
		underline: {
			iconName: "text-style-underline" as IconName,
			label: labelUnderline,
			isActive: isUnderlined,
			isDisabled: isUnderlinedDisabled || !areFontFamiliesLoaded,
			onClick: onUnderlineClick,
		},
	} as const;
}
